import { useState, useEffect, useRef, useCallback } from "react";
import { messagesAPI, callsAPI } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import {
  MessageSquare, Send, ChevronLeft, Video, Phone, PhoneOff,
  Crown, Clock, Eye, ShieldCheck, Maximize2, Minimize2, Trash2
} from "lucide-react";
import { toast } from "sonner";

export function MessagesView({ user, t, onPremium }) {
  const [conversations, setConversations] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [incomingCall, setIncomingCall] = useState(null);
  const [activeCall, setActiveCall] = useState(null);
  const [showVideo, setShowVideo] = useState(false);
  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);
  const jitsiRef = useRef(null);
  const jitsiContainerRef = useRef(null);

  const isHotel = user?.role === "hotel";

  useEffect(() => { loadConversations(); }, []);

  useEffect(() => {
    if (activeConv) {
      loadMessages(activeConv.conversation_id);
      pollRef.current = setInterval(() => loadMessages(activeConv.conversation_id), 5000);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeConv?.conversation_id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Poll for incoming calls
  useEffect(() => {
    const checkCalls = async () => {
      try {
        const res = await callsAPI.getPending();
        if (res.data?.length > 0) setIncomingCall(res.data[0]);
      } catch {}
    };
    const interval = setInterval(checkCalls, 4000);
    checkCalls();
    return () => clearInterval(interval);
  }, []);

  const loadConversations = async () => {
    try {
      const res = await messagesAPI.getConversations();
      setConversations(res.data);
    } catch {} finally { setLoading(false); }
  };

  const loadMessages = async (convId) => {
    try {
      const res = await messagesAPI.getMessages(convId);
      setMessages(res.data.messages || []);
    } catch {}
  };

  const handleSend = async () => {
    if (!newMsg.trim() || !activeConv) return;
    setSending(true);
    try {
      await messagesAPI.sendMessage({ conversation_id: activeConv.conversation_id, content: newMsg.trim() });
      setNewMsg("");
      await loadMessages(activeConv.conversation_id);
      loadConversations();
    } catch { toast.error("Error"); } finally { setSending(false); }
  };

  const handleDeleteConversation = async (convId) => {
    try {
      await messagesAPI.deleteConversation(convId);
      setConversations(prev => prev.filter(c => c.conversation_id !== convId));
      setDeleteTarget(null);
      toast.success(t("msg.deleted"));
    } catch { toast.error("Error"); }
  };

  const destroyJitsi = useCallback(() => {
    if (jitsiRef.current) {
      try { jitsiRef.current.dispose(); } catch {}
      jitsiRef.current = null;
    }
  }, []);

  const startJitsi = useCallback((roomId, displayName) => {
    destroyJitsi();
    setShowVideo(true);
    // Small delay to ensure container is rendered
    setTimeout(() => {
      if (!jitsiContainerRef.current || !window.JitsiMeetExternalAPI) {
        toast.error("Jitsi not loaded yet. Please try again.");
        return;
      }
      try {
        const api = new window.JitsiMeetExternalAPI("meet.jit.si", {
          roomName: `staffera_${roomId}`,
          parentNode: jitsiContainerRef.current,
          width: "100%",
          height: "100%",
          userInfo: { displayName: displayName || user?.name || "User" },
          configOverwrite: {
            startWithAudioMuted: false,
            startWithVideoMuted: false,
            prejoinPageEnabled: false,
            disableDeepLinking: true,
            toolbarButtons: [
              'camera', 'chat', 'closedcaptions', 'desktop',
              'fullscreen', 'hangup', 'microphone', 'raisehand',
              'settings', 'tileview', 'toggle-camera',
            ],
          },
          interfaceConfigOverwrite: {
            SHOW_JITSI_WATERMARK: false,
            SHOW_WATERMARK_FOR_GUESTS: false,
            SHOW_BRAND_WATERMARK: false,
            TOOLBAR_ALWAYS_VISIBLE: true,
            DISABLE_JOIN_LEAVE_NOTIFICATIONS: true,
          },
        });
        api.addListener("readyToClose", () => {
          handleEndCall();
        });
        jitsiRef.current = api;
      } catch (err) {
        console.error("Jitsi error:", err);
        toast.error("Error starting video call");
      }
    }, 300);
  }, [destroyJitsi, user?.name]);

  const handleVideoCall = async () => {
    if (!activeConv) return;
    try {
      const res = await callsAPI.initiate(activeConv.conversation_id);
      setActiveCall(res.data);
      toast.success(t("msg.calling"));
      // Poll call status
      const pollCall = setInterval(async () => {
        try {
          const cr = await callsAPI.get(res.data.call_id);
          if (cr.data.status === "declined") {
            clearInterval(pollCall);
            setActiveCall(null);
            toast.error(t("msg.call_declined"));
          } else if (cr.data.status === "ended") {
            clearInterval(pollCall);
            setActiveCall(null);
            destroyJitsi();
            setShowVideo(false);
          } else if (cr.data.status === "active") {
            clearInterval(pollCall);
            setActiveCall(cr.data);
            startJitsi(cr.data.room_id, user?.name);
          }
        } catch { clearInterval(pollCall); setActiveCall(null); }
      }, 2000);
    } catch (e) {
      if (e.response?.status === 403) toast.error(t("msg.video_premium_only"));
      else toast.error("Error");
    }
  };

  const handleAcceptCall = async () => {
    if (!incomingCall) return;
    try {
      await callsAPI.update(incomingCall.call_id, { action: "accept" });
      setActiveCall(incomingCall);
      setIncomingCall(null);
      startJitsi(incomingCall.room_id, user?.name);
    } catch { toast.error("Error"); }
  };

  const handleDeclineCall = async () => {
    if (!incomingCall) return;
    try {
      await callsAPI.update(incomingCall.call_id, { action: "decline" });
      setIncomingCall(null);
    } catch {}
  };

  const handleEndCall = async () => {
    if (!activeCall) return;
    try {
      await callsAPI.update(activeCall.call_id, { action: "end" });
    } catch {}
    setActiveCall(null);
    destroyJitsi();
    setShowVideo(false);
    toast.success(t("msg.call_ended"));
  };

  // Cleanup Jitsi on unmount
  useEffect(() => { return () => destroyJitsi(); }, [destroyJitsi]);

  const otherName = (conv) => isHotel ? conv.candidate_name : conv.hotel_name;

  if (loading) {
    return <div className="flex items-center justify-center py-12"><div className="w-6 h-6 border-2 border-gold-500 border-t-transparent rounded-full animate-spin" /></div>;
  }

  // Conversation list
  if (!activeConv) {
    return (
      <div className="space-y-3" data-testid="messages-list">
        <h3 className="font-sans text-lg font-bold text-slate-900">{t("msg.conversations")}</h3>
        {conversations.length === 0 ? (
          <Card className="border-slate-200 bg-white"><CardContent className="py-8 text-center">
            <MessageSquare className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500 font-body">{t("msg.no_conversations")}</p>
          </CardContent></Card>
        ) : (
          conversations.map(conv => (
            <Card key={conv.conversation_id} className="border-slate-200 bg-white shadow-sm hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => setActiveConv(conv)} data-testid={`conv-${conv.conversation_id}`}>
              <CardContent className="py-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-navy-900 flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-sans font-bold text-sm">{(otherName(conv) || "?")[0]?.toUpperCase()}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <p className="font-sans font-semibold text-slate-900 text-sm truncate">{otherName(conv)}</p>
                    <div className="flex items-center gap-2">
                      {conv.unread > 0 && (
                        <Badge className="bg-gold-500 text-white text-xs font-bold min-w-[20px] h-5 flex items-center justify-center" data-testid={`unread-${conv.conversation_id}`}>
                          {conv.unread}
                        </Badge>
                      )}
                      <button
                        className="p-1.5 rounded-md text-slate-300 hover:text-red-500 hover:bg-red-50 transition-colors"
                        onClick={(e) => { e.stopPropagation(); setDeleteTarget(conv); }}
                        data-testid={`delete-conv-${conv.conversation_id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                  <p className="text-xs text-slate-500 font-body truncate">{conv.last_message || "..."}</p>
                </div>
              </CardContent>
            </Card>
          ))
        )}

        {/* Delete confirmation dialog */}
        <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
          <DialogContent className="sm:max-w-sm p-0 overflow-hidden border-0 rounded-lg shadow-2xl" data-testid="delete-conv-dialog">
            <DialogTitle className="sr-only">{t("msg.delete_title")}</DialogTitle>
            <div className="px-6 pt-6 pb-4 text-center">
              <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-red-100 flex items-center justify-center">
                <Trash2 className="w-6 h-6 text-red-500" />
              </div>
              <h3 className="font-sans font-bold text-lg text-slate-900 mb-1">{t("msg.delete_title")}</h3>
              <p className="text-sm text-slate-500 font-body">{t("msg.delete_desc")}</p>
            </div>
            <div className="px-6 pb-5 flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setDeleteTarget(null)} data-testid="delete-conv-cancel">
                {t("common.cancel")}
              </Button>
              <Button className="flex-1 bg-red-500 hover:bg-red-600 text-white" onClick={() => handleDeleteConversation(deleteTarget?.conversation_id)} data-testid="delete-conv-confirm">
                {t("msg.delete_confirm")}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Chat view
  return (
    <div className="flex flex-col h-[calc(100vh-120px)]" data-testid="chat-view">
      {/* Chat header */}
      <div className="flex items-center gap-3 pb-3 border-b border-slate-200">
        <button onClick={() => { setActiveConv(null); loadConversations(); }} className="text-slate-500 hover:text-gold-600" data-testid="back-to-conversations">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="w-8 h-8 rounded-full bg-navy-900 flex items-center justify-center">
          <span className="text-white font-bold text-xs">{(otherName(activeConv) || "?")[0]?.toUpperCase()}</span>
        </div>
        <p className="font-sans font-semibold text-slate-900 text-sm flex-1 truncate">{otherName(activeConv)}</p>
        <Button variant="outline" size="sm" className="h-9 px-3 border-slate-300" onClick={handleVideoCall}
          disabled={!!activeCall} data-testid="video-call-btn">
          <Video className="w-4 h-4 mr-1" /> {t("msg.video_call")}
        </Button>
      </div>

      {/* Active call bar */}
      {activeCall && !showVideo && (
        <div className="bg-emerald-500 text-white px-4 py-3 flex items-center justify-between rounded-lg mt-2" data-testid="active-call-bar">
          <div className="flex items-center gap-2">
            <Phone className="w-4 h-4 animate-pulse" />
            <span className="text-sm font-semibold font-body">{activeCall.status === "pending" ? t("msg.calling") : t("msg.in_call")}</span>
          </div>
          <Button size="sm" className="bg-red-500 hover:bg-red-600 text-white h-8" onClick={handleEndCall} data-testid="end-call-btn">
            <PhoneOff className="w-4 h-4 mr-1" /> {t("msg.end_call")}
          </Button>
        </div>
      )}

      {/* Jitsi Video Call Dialog */}
      <Dialog open={showVideo} onOpenChange={() => {}}>
        <DialogContent className="max-w-[95vw] w-[95vw] h-[90vh] p-0 border-0 rounded-xl overflow-hidden bg-navy-900" data-testid="video-call-dialog">
          <DialogTitle className="sr-only">{t("msg.video_call")}</DialogTitle>
          <div className="absolute top-3 right-3 z-50 flex gap-2">
            <Button size="sm" className="bg-red-500 hover:bg-red-600 text-white h-9 px-4 shadow-lg" onClick={handleEndCall} data-testid="video-end-call-btn">
              <PhoneOff className="w-4 h-4 mr-1" /> {t("msg.end_call")}
            </Button>
          </div>
          <div ref={jitsiContainerRef} className="w-full h-full" data-testid="jitsi-container" />
        </DialogContent>
      </Dialog>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto py-4 space-y-3" data-testid="messages-container">
        {messages.map(msg => {
          const isMine = msg.sender_id === user?.user_id;
          return (
            <div key={msg.message_id} className={`flex ${isMine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[75%] px-4 py-2.5 rounded-2xl ${isMine ? "bg-gold-500 text-white" : "bg-slate-100 text-slate-900"}`}
                data-testid={`msg-${msg.message_id}`}>
                <p className="text-sm font-body whitespace-pre-wrap break-words">{msg.content}</p>
                <p className={`text-[10px] mt-1 ${isMine ? "text-navy-900/60" : "text-slate-400"}`}>
                  {new Date(msg.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </p>
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="flex gap-2 pt-3 border-t border-slate-200">
        <Input value={newMsg} onChange={(e) => setNewMsg(e.target.value)}
          placeholder={t("msg.type_message")} className="flex-1 h-11 font-body"
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
          data-testid="message-input" />
        <Button className="bg-gold-500 hover:bg-gold-600 text-white h-11 px-4" onClick={handleSend} disabled={sending || !newMsg.trim()} data-testid="send-message-btn">
          <Send className="w-4 h-4" />
        </Button>
      </div>

      {/* Incoming call dialog */}
      <Dialog open={!!incomingCall} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-sm p-0 overflow-hidden border-0 rounded-lg" data-testid="incoming-call-dialog">
          <DialogTitle className="sr-only">{t("msg.incoming_call")}</DialogTitle>
          <div className="bg-navy-900 px-6 py-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-500/20 flex items-center justify-center animate-pulse">
              <Phone className="w-8 h-8 text-emerald-400" />
            </div>
            <h3 className="font-sans text-lg font-bold text-white mb-1">{t("msg.incoming_call")}</h3>
            <p className="text-sm text-slate-300 font-body">{incomingCall?.caller_name || incomingCall?.hotel_name}</p>
          </div>
          <div className="px-6 py-5 flex gap-3">
            <Button className="flex-1 bg-red-500 hover:bg-red-600 text-white" onClick={handleDeclineCall} data-testid="decline-call-btn">
              <PhoneOff className="w-4 h-4 mr-2" /> {t("msg.decline")}
            </Button>
            <Button className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white" onClick={handleAcceptCall} data-testid="accept-call-btn">
              <Phone className="w-4 h-4 mr-2" /> {t("msg.accept")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function ProfileViewsCard({ t, isPremium, onPremium }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const { viewsAPI } = await import("@/lib/api");
        const res = await viewsAPI.getStats();
        setStats(res.data);
      } catch {} finally { setLoading(false); }
    };
    load();
  }, []);

  if (loading) return null;
  if (!stats) return null;

  return (
    <Card className="border-slate-200 bg-white shadow-sm" data-testid="profile-views-card">
      <CardContent className="py-5">
        <h3 className="font-sans font-semibold text-slate-900 text-sm mb-4 flex items-center gap-2">
          <Eye className="w-4 h-4 text-gold-500" /> {t("views.title")}
        </h3>

        {stats.views_total === 0 ? (
          <p className="text-xs text-slate-400 font-body">{t("views.no_views")}</p>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-3 mb-4">
              {[
                { label: t("views.this_week"), val: stats.views_week },
                { label: t("views.this_month"), val: stats.views_month },
                { label: t("views.total"), val: stats.views_total },
              ].map(({ label, val }) => (
                <div key={label} className="text-center">
                  <p className="font-sans text-2xl font-bold text-navy-900">{val}</p>
                  <p className="text-[11px] text-slate-500 font-body">{label}</p>
                </div>
              ))}
            </div>

            {stats.verified_viewers_count > 0 && (
              <div className="flex items-center gap-2 mb-4 bg-emerald-50 border border-emerald-200 rounded-lg p-2.5" data-testid="verified-viewers-count">
                <ShieldCheck className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                <p className="text-xs text-emerald-700 font-body font-semibold">{stats.verified_viewers_count} {t("views.verified_hotels")}</p>
              </div>
            )}

            {isPremium && stats.recent_viewers?.length > 0 ? (
              <div className="space-y-2">
                <p className="text-xs font-semibold text-slate-600 font-body">{t("views.recent_viewers")}</p>
                {stats.recent_viewers.map((v) => (
                  <div key={v.hotel_name} className="flex items-center gap-2 text-sm">
                    <div className="w-6 h-6 rounded-full bg-navy-900/10 flex items-center justify-center flex-shrink-0">
                      <span className="text-navy-900 font-bold text-[10px]">{(v.hotel_name || "?")[0]?.toUpperCase()}</span>
                    </div>
                    <span className="font-body text-slate-700 flex-1 truncate">{v.hotel_name}</span>
                    {v.hotel_verified && <ShieldCheck className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />}
                    <span className="text-[10px] text-slate-400 font-body flex items-center gap-1"><Clock className="w-3 h-3" />{new Date(v.viewed_at).toLocaleDateString()}</span>
                  </div>
                ))}
              </div>
            ) : !isPremium ? (
              <button onClick={onPremium}
                className="w-full bg-navy-900/5 border border-navy-900/10 rounded-lg p-3 text-center hover:bg-navy-900/10 transition-colors"
                data-testid="views-premium-upsell">
                <Crown className="w-4 h-4 text-gold-500 mx-auto mb-1" />
                <p className="text-xs text-slate-600 font-body">{t("views.premium_unlock")}</p>
              </button>
            ) : null}
          </>
        )}
      </CardContent>
    </Card>
  );
}
